import { of } from 'rxjs'
import { Epic } from 'redux-observable'
import { delay, filter, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { omit } from 'lodash-es'
import { pathOr } from 'ramda'
import { EpicDependencies, RootState } from 'src/state/types'
import { events as uiInvoiceEvents, UiInvoiceEvent } from '../../ui/invoices/state'
import { multiUserOperators } from '../../multiUser/state'
import { ENDPOINTS } from './constants'
import { events, InvoiceEvent } from './state'
import * as transformers from './transformers'
import {
  TBulkInvoicesResponse,
  TInvoice,
  TInvoiceIdResult,
  TInvoiceSendResult,
  TInvoiceTypeSummary,
  TInvoiceNotification,
  TUpdateInvoiceBody,
} from './types'

type InvoiceEpic = Epic<InvoiceEvent, InvoiceEvent, RootState, EpicDependencies>

/**
 * `Epic<In, Out>` constrains `Out extends In`, so an epic that is triggered by a
 * `ui/invoices` action has to list both unions in both generics.
 */
type UiTriggeredInvoiceEpic = Epic<
  InvoiceEvent | UiInvoiceEvent,
  InvoiceEvent | UiInvoiceEvent,
  RootState,
  EpicDependencies
>

/**
 * @todo move to payments feature module?
 */
export const bulkActionDelete: UiTriggeredInvoiceEpic = action$ =>
  action$.pipe(
    filter(isOfType(uiInvoiceEvents.bulkActionDelete.type)),
    pluck('payload'),
    map(invoiceIds => events.deleteInvoices_request(invoiceIds)),
    tag('invoice/epic/bulkActionDelete'),
  )

export const bulkActionRestore: UiTriggeredInvoiceEpic = action$ =>
  action$.pipe(
    filter(isOfType(uiInvoiceEvents.bulkActionRestore.type)),
    pluck('payload'),
    map(invoiceIds => events.restoreInvoices_request(invoiceIds)),
    tag('invoice/epic/bulkActionRestore'),
  )

/**
 * @todo move to payments feature module?
 */
export const bulkActionSend: UiTriggeredInvoiceEpic = action$ =>
  action$.pipe(
    filter(isOfType(uiInvoiceEvents.bulkActionSend.type)),
    delay(500), // delay for animation
    pluck('payload'),
    map(invoiceIds => events.sendInvoices_request(invoiceIds)),
    tag('invoice/epic/bulkActionSend'),
  )

export const apiFetchInvoices: InvoiceEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoices_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.INVOICE_BASE_V2, state$, action.payload).pipe(
        pluck('response', 'invoices'),
        map((res: TInvoice[]) => events.invoices_success(transformers.transformInvoiceResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/apiFetchInvoices'),
  )

export const apiFetchInvoice: InvoiceEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoice_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.INVOICE_ENTITY, state$, action.payload).pipe(
        pluck('response'),
        map((res: TInvoice) => events.invoice_success(transformers.transformInvoiceResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/apiFetchInvoice'),
  )

export const updateInvoice: InvoiceEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoiceUpdate_request.type)),
    mergeMap(action => {
      const { id, values } = action.payload
      /**
       * The response-only fields go, and the customer is flattened into the two keys the
       * request carries.
       */
      const body: TUpdateInvoiceBody = omit(
        {
          ...values,
          customer: values.customer?.partyId,
          customerTag: values.customer?.customerTag,
        },
        ['balance', 'invoiceTypeName', 'property', 'reoccurring', 'tags'],
      )
      return put(ENDPOINTS.INVOICE_ENTITY, state$, transformers.transformUpdateInvoiceRequest(body), { id }).pipe(
        pluck('response'),
        map((res: TInvoice) => events.invoiceUpdate_success(transformers.transformInvoiceResponse(res))),
        catchRestError(action),
      )
    }),
    tag('invoice/epics/updateInvoice'),
  )

export const createOnceOffInvoice: InvoiceEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.createOnceOffInvoice_request.type)),
    mergeMap(action => {
      const { values, sendAfterCreate } = action.payload
      return post(ENDPOINTS.INVOICE_BASE, state$, transformers.transformNewInvoiceRequest(values), {}).pipe(
        pluck('response'),
        map((res: TInvoice) =>
          events.createOnceOffInvoice_success(transformers.transformNewInvoiceResponse(res), action.meta ?? null),
        ),
        /** @todo I don't think this is working as intended... */
        mergeMap(event => {
          const id = sendAfterCreate ? event.payload.id : undefined
          return id ? of(event, events.sendInvoices_request([id])) : of(event)
        }),
        catchRestError(action, false),
      )
    }),
    tag('invoice/epics/createOnceOffInvoice'),
  )

export const fetchInvoiceTypes: InvoiceEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoiceTypes_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_INVOICE_TYPES, state$, {}).pipe(
        pluck('response', 'invoiceTypeSummaries'),
        map((res: TInvoiceTypeSummary[]) =>
          events.invoiceTypes_success(transformers.transformInvoiceTypesResponse(res)),
        ),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/fetchInvoiceTypes'),
  )

export const sendInvoices: InvoiceEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.sendInvoices_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.SEND_INVOICES, state$, { invoiceIds: action.payload }, {}).pipe(
        pluck('response', 'invoiceResults'),
        map((res: TInvoiceSendResult[]) => events.sendInvoices_success(res)),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/sendInvoices'),
  )

export const deleteInvoices: InvoiceEpic = (action$, state$, { removeWithBody, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.deleteInvoices_request.type)),
    delay(500), // delay for animation
    mergeMap(action =>
      removeWithBody(ENDPOINTS.DELETE_INVOICES, state$, { invoiceIds: action.payload }, {}).pipe(
        pluck('response', 'invoiceResults'),
        map((res: TInvoiceIdResult[]) => events.deleteInvoices_success(res)),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/deleteInvoices'),
  )

export const restoreInvoices: InvoiceEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.restoreInvoices_request.type)),
    delay(500), // delay for animation
    mergeMap(action =>
      post(ENDPOINTS.RESTORE_INVOICES, state$, { invoiceIds: action.payload }, {}).pipe(
        pluck('response', 'invoiceResults'),
        map((res: TInvoiceIdResult[]) => events.restoreInvoices_success(res)),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/restoreInvoices'),
  )

export const fetchNotifications: InvoiceEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.notifications_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.NOTIFICATIONS, state$, {}).pipe(
        pluck('response'),
        map((res: TInvoiceNotification[]) => events.notifications_success(res)),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/fetchNotifications'),
  )

export const fetchDraftInvoices: InvoiceEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.draftInvoices_request.type)),
    mergeMap(action => {
      const { id } = action.payload
      return get(ENDPOINTS.FETCH_DRAFT_INVOICES, state$, { id }).pipe(
        pluck('response'),
        map(res => {
          // `InvoicesByPortfolio` is `{ invoices }`; the slice keys drafts per portfolio id.
          const draftInvoices: string[] = pathOr([], ['invoices'], res)
          return events.draftInvoices_success({ draftInvoices, id })
        }),
        catchRestError(action),
      )
    }),
    tag('invoice/epics/fetchDraftInvoices'),
  )

/** @todo add test */
export const fetchBulkInvoices: InvoiceEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.fetchBulkInvoices_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.BULK_INVOICES, state$, {}).pipe(
        pluck('response'),
        map((res: TBulkInvoicesResponse) =>
          events.fetchBulkInvoices_success(transformers.transformGetBulkInvoicesResponse(res)),
        ),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/fetchBulkInvoices'),
  )

/** @todo add test */
export const deleteBulkInvoices: InvoiceEpic = (action$, state$, { remove, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.deleteBulkInvoices_request.type)),
    mergeMap(action =>
      remove(ENDPOINTS.BULK_INVOICES, state$, {}).pipe(
        pluck('response'),
        map(res => events.deleteBulkInvoices_success(res)),
        catchRestError(action),
      ),
    ),
    tag('invoice/epics/deleteBulkInvoices'),
  )
